import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useAuth } from "@/lib/auth";
import { hasMinRole, type SystemRole } from "@/lib/rbac";
import { useUpsertSaasConnector } from "@/hooks/use-saas-connectors";
import { clearStepFailures, reportStepFailed } from "./analytics";

/**
 * Step 1 is done when the org has its product registered (saas_connectors),
 * never because the agent ticked its todos: a run can tick them all without
 * building anything (no platform tools, a tool that kept failing), and a
 * connector it links may not be one this org can register. Every surface that
 * shows the build's end reads it from here, so the build screen, the build card
 * and the setup panel cannot disagree.
 */
export type BuildOutcome =
  | { kind: "running" }
  | { kind: "registering" }
  | { kind: "connected" }
  | { kind: "failed"; reason: "nothing-built" | "needs-admin" | "rejected"; detail: string | null };

interface Attempt {
  status: "pending" | "success" | "error";
  error: string | null;
}

/**
 * Each connector's registration this page load, shared by every screen that
 * shows the build. A connector is sent once; only the person retrying sends it
 * again. (A retry on failure from a render effect is what once sent hundreds of
 * PUTs a minute until the gateway answered 429.)
 */
const attempts = new Map<string, Attempt>();
const listeners = new Set<() => void>();
let version = 0;
function setAttempt(id: string, a: Attempt): void {
  attempts.set(id, a);
  version++;
  for (const l of listeners) l();
}
/** A run that starts again (the person sent the agent back) may fix what was refused, so it gets a fresh try. */
function forgetFailures(): void {
  let changed = false;
  for (const [id, a] of attempts) if (a.status === "error") { attempts.delete(id); changed = true; }
  if (changed) { version++; for (const l of listeners) l(); }
}
const needsYouReported = new Set<string>();
/** A build that ended needing a person, reported once per session when the screen shows it. A refused registration is reported where it is refused, with its status. */
/** `message` is what the screen tells the person, so the event says which error they saw. */
export function reportBuildNeedsYou(reason: "nothing-built" | "needs-admin" | "blocked", message: string | null = null): void {
  if (needsYouReported.has(reason)) return;
  needsYouReported.add(reason);
  reportStepFailed(
    "building",
    1,
    reason === "needs-admin" ? "product_registration_needs_admin" : reason === "blocked" ? "agent_blocked" : "product_registration_nothing_built",
    message ? { message } : undefined,
  );
}
function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

function useCanRegister(): boolean {
  const { userContext } = useAuth();
  return hasMinRole((userContext?.role ?? "end_user") as SystemRole, "admin");
}

export function buildOutcome(o: {
  buildDone: boolean;
  productDone: boolean;
  connectorId: string | null;
  canRegister: boolean;
  attempt: Attempt | null;
}): BuildOutcome {
  if (o.productDone) return { kind: "connected" };
  if (!o.buildDone) return { kind: "running" };
  if (!o.connectorId) return { kind: "failed", reason: "nothing-built", detail: null };
  if (!o.canRegister) return { kind: "failed", reason: "needs-admin", detail: null };
  // A success still waits for the setup progress to read the new registration.
  if (!o.attempt || o.attempt.status !== "error") return { kind: "registering" };
  return { kind: "failed", reason: "rejected", detail: o.attempt.error };
}

/**
 * Registers the connector a finished build made, once per page load, and says
 * where the build stands. `connectorId` is the build's connector while the
 * build is done and step 1 is not, otherwise null.
 */
export function useProductRegistration(opts: { buildDone: boolean; productDone: boolean; connectorId: string | null }): {
  outcome: BuildOutcome;
  retry: () => void;
} {
  const canRegister = useCanRegister();
  const { mutateAsync } = useUpsertSaasConnector();
  useSyncExternalStore(subscribe, () => version);
  const id = opts.buildDone && !opts.productDone ? opts.connectorId : null;

  const send = useCallback((connectorId: string) => {
    setAttempt(connectorId, { status: "pending", error: null });
    mutateAsync({ connectorId, scopes: [] }).then(
      () => {
        clearStepFailures("building", "product_registration_rejected");
        setAttempt(connectorId, { status: "success", error: null });
      },
      (err: unknown) => {
        reportStepFailed("building", 1, "product_registration_rejected", err);
        setAttempt(connectorId, { status: "error", error: err instanceof Error ? err.message : String(err) });
      },
    );
  }, [mutateAsync]);

  useEffect(() => { if (!opts.buildDone) forgetFailures(); }, [opts.buildDone]);
  useEffect(() => {
    if (!id || !canRegister || attempts.has(id)) return;
    send(id);
  }, [id, canRegister, send]);

  const outcome = buildOutcome({
    buildDone: opts.buildDone,
    productDone: opts.productDone,
    connectorId: opts.connectorId,
    canRegister,
    attempt: id ? attempts.get(id) ?? null : null,
  });
  const retry = () => { if (id && canRegister && attempts.get(id)?.status !== "pending") send(id); };
  return { outcome, retry };
}

/** The message that sends a run which ended without a usable connector back to the agent. */
export function unfinishedBuildMessage(outcome: BuildOutcome, productName: string): string {
  const why =
    outcome.kind === "failed" && outcome.reason === "rejected"
      ? `fastn could not register the connector you linked as my product${outcome.detail ? ` (${outcome.detail})` : ""}.`
      : `no connector for ${productName} was built in my org.`;
  return [
    `You marked the build done, but ${why}`,
    `Build the ${productName} connector in my org, with its sign-in and the actions, and link it in the todo list when it really exists.`,
    `If you cannot (a tool is missing or keeps failing), tell me what is missing instead of completing the todos.`,
  ].join(" ");
}
